import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useEvents } from '../stores/events'
import { Plus, CalendarDays, ArrowRight } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

const STATUS = {
  draft: { label: 'Entwurf', cls: 'bg-slate-700 text-slate-300' },
  published: { label: 'Live', cls: 'bg-emerald-500/20 text-emerald-400' },
  sold_out: { label: 'Ausverkauft', cls: 'bg-amber-500/20 text-amber-400' },
  cancelled: { label: 'Abgesagt', cls: 'bg-red-500/20 text-red-400' },
  completed: { label: 'Beendet', cls: 'bg-slate-700 text-slate-400' },
  archived: { label: 'Archiviert', cls: 'bg-slate-800 text-slate-500' },
}

export function EventsPage() {
  const { events, total, loading, fetchEvents } = useEvents()

  useEffect(() => {
    fetchEvents({ limit: 50 })
  }, [])

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Events</h1>
          <p className="text-slate-400 text-sm mt-1">{total} Events gesamt</p>
        </div>
        <Link
          to="/events/new"
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neues Event
        </Link>
      </div>

      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-xl">
          <CalendarDays className="w-16 h-16 text-slate-700 mx-auto mb-4" />
          <h2 className="text-xl text-white font-semibold mb-2">Noch keine Events</h2>
          <p className="text-slate-400 mb-6">Erstelle dein erstes Event und starte den Ticketverkauf.</p>
          <Link
            to="/events/new"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Erstes Event erstellen
          </Link>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-auto max-h-[calc(100vh-12rem)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-slate-900">
              <tr className="border-b border-slate-800">
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Datum</th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Event</th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Typ</th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Kapazität</th>
                <th scope="col" className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th scope="col" className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {events.map((event) => {
                const status = STATUS[event.status as keyof typeof STATUS] || STATUS.draft
                return (
                  <tr key={event.id} className="hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm text-white font-medium">
                        {format(new Date(event.date), 'd. MMM', { locale: de })}
                      </p>
                      <p className="text-xs text-slate-500">
                        {format(new Date(event.date), 'EEEE', { locale: de })}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/events/${event.id}`} className="text-white hover:text-indigo-400 font-medium">
                        {event.title}
                      </Link>
                      {event.subtitle && <p className="text-xs text-slate-400">{event.subtitle}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300 capitalize">{event.event_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{event.capacity}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.cls}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/events/${event.id}`} className="text-slate-600 hover:text-white">
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
